import * as React from 'react';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import authService from '../api-authorization/AuthorizeService';
import { ResponseType, TokenRefreshContext } from '../Contexts/TokenRefreshContext';
import CustomCollapse from '../Utilities/CustomCollapse';
import DeleteIcon from '@mui/icons-material/Delete';
import FileViewModel from './Viewmodels/FileViewModel';
import UploadFileDialog from './UploadFileDialog';
import DownloadIcon from '@mui/icons-material/Download';
import PermissionCheck from '../Utilities/PermissionCheck';
import { Permission } from '../Permissions/Permissions';
import ResponsiveTable from '../Utilities/ResponsiveTable';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TableBody from '@mui/material/TableBody';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

interface IProps {
    wseId: number;
    setFiles: (history: FileViewModel[], markDirty?: boolean) => void;
    files: FileViewModel[];
    disableFields: boolean;
}

export default function AttachedFiles(props: IProps) {
    const { wseId, files, setFiles, disableFields } = props;
    const { crabFetch } = React.useContext(TokenRefreshContext);
    const [loading, setLoading] = React.useState(false);
    const [collapse, setCollapse] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [role, setRole] = React.useState<string>('');

    React.useEffect(() => {
        getData();
        checkRole();
    }, []);

    const checkRole = async () => {
        const user = await authService.getUser();

        if (user && user.sub) {
            setRole(user.role ? user.role : user.userType ? user.userType : '');
        }
    }

    const getData = async () => {
        setLoading(true);
        const token = await authService.getAccessToken();

        await crabFetch(`Wse/GetFiles?wseId=${wseId}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.JSON,
            (data: any) => {
                setFiles(data, false);
            }
        );
        setLoading(false);
    }

    const removeRow = (index: number) => {
        const newArr = [...files];
        newArr.splice(index, 1);
        setFiles(newArr, true);
    }

    const retrunFile = (newFile: FileViewModel) => {
        const newArr = [...files];
        newArr.push(newFile);
        setFiles(newArr, true);
    }

    const updateDescription = (index: number, description: string) => {
        const newArr = [...files];
        newArr[index].description = description;
        setFiles(newArr, true);
    }

    const closeDialog = (refresh: boolean) => {
        setOpen(false);
    }

    const download = async (id: number, name: string) => {
        if (id === 0) {
            return;
        }
        const token = await authService.getAccessToken();
        setLoading(true);

        crabFetch(`Wse/DownloadFile?id=${id}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.Blob,
            ((blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                var a = document.createElement('a');
                a.download = name;
                a.href = url;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                getData();
            })
        );
    }

    return (
        <Paper>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <CustomCollapse showMore={collapse} setShowMore={setCollapse} title={'Attached Files'} count={disableFields ? files.length : undefined}>
                        {!loading && files.length > 0 &&
                            <TableContainer>
                                <ResponsiveTable
                                    aria-labelledby="tableTitle"
                                    size={'medium'}
                                    aria-label="files table"
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>File Name</TableCell>
                                            <TableCell />
                                            <TableCell>Description</TableCell>
                                            {role !== "Client" &&
                                                <TableCell></TableCell>
                                            }
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {files.map((file: FileViewModel, index: number) => (
                                            <TableRow key={'file_' + file.id}>
                                                <TableCell sx={{ backgroundColor: '#fff !important' }}>
                                                    <Typography>{file.name !== null ? file.name : file.attachment !== null ? file.attachment.name : ""}</Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ backgroundColor: '#fff !important', width: 64 }}>
                                                    <IconButton size="small" onClick={() => download(file.id === null ? 0 : file.id, file.name !== null ? file.name : file.attachment !== null ? file.attachment.name : "")}><DownloadIcon /></IconButton>
                                                </TableCell>
                                                <TableCell sx={{ backgroundColor: '#fff !important' }}>
                                                    <TextField disabled={disableFields || role === 'Client'} name="description" value={file.description} onChange={(e) => updateDescription(index, e.target.value)} required fullWidth size="small" />
                                                </TableCell>
                                                {role !== "Client" &&
                                                    <TableCell sx={{ backgroundColor: '#fff !important' }}>
                                                        <Typography color="error">{!file.fileInBlob && 'File not found. Please upload again.'}</Typography>
                                                    </TableCell>
                                                }
                                                <PermissionCheck permission={Permission.CreateWse}>
                                                    <TableCell align="right" sx={{ backgroundColor: '#fff !important', width: 64 }}>
                                                        <IconButton size="small" onClick={() => removeRow(index)} disabled={disableFields}><DeleteIcon /></IconButton>
                                                    </TableCell>
                                                </PermissionCheck>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </ResponsiveTable>
                            </TableContainer>
                        }
                        <PermissionCheck permission={Permission.CreateWse}>
                            {!disableFields &&
                                <Grid sx={{ pt: 1 }}>
                                    <Button onClick={() => setOpen(true)} variant="contained">Upload File</Button>
                                </Grid>
                            }
                        </PermissionCheck>
                    </CustomCollapse>
                    <UploadFileDialog open={open} onClose={closeDialog} returnFile={retrunFile}></UploadFileDialog>
                </Grid>
            </Grid>
        </Paper >
    );
}
